import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowRight, CalendarCheck, ChevronRight, Clock, ListOrdered } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { GuideBlocks, GuideImg, guideLink } from "@/components/guides/GuideBody";
import { renderInline } from "@/lib/miniMarkdown";
import { langFromSearch } from "@/lib/seo/site";
import {
  AUDIENCES,
  GUIDES_PATH,
  formatGuideDate,
  getGuide,
  guideLangs,
  guidePath,
  readingMinutes,
  type Guide,
  type GuideAudience,
} from "@/content/guides";
import { authorAnchor, findAuthor, type GuideAuthor } from "@/content/guides/authors";
import { GUIDE_IMAGES } from "@/content/guides/images";
import { GuidesOnly } from "./GuidesOnly";

// One guide: /guides/<slug>. Server-rendered from src/content/guides/<slug>/<lang>.md; the <head> (title, canonical,
// Open Graph, Article / BreadcrumbList / FAQPage JSON-LD) comes from RouteMeta via src/lib/seo/pages.ts.

/** The table of contents: the guide's H2s. */
const tocOf = (g: Guide) => g.blocks.flatMap((b) => (b.type === "heading" && b.level === 2 ? [{ id: b.id, text: b.text.replace(/\*\*|`/g, "") }] : []));

const CTA: Record<GuideAudience, { heading: string; body: string; primary: { label: string; to: string }; secondary?: { label: string; to: string } }> = {
  participants: {
    heading: "Ready to look for your photos?",
    body: "Search event galleries on Photohawk by name, then find yourself with your bib number or a selfie.",
    primary: { label: "Find your photos", to: "/find-your-photos" },
  },
  photographers: {
    heading: "Get your next event online sooner",
    body: "Upload with the web uploader on any plan, let people find themselves by selfie or bib, and sell or share every gallery your way.",
    primary: { label: "Start free", to: "/register" },
    secondary: { label: "See pricing", to: "/pricing" },
  },
  organisers: {
    heading: "Offering photo search at your event?",
    body: "Tell us about your event and we'll help you set up galleries, notices and face search the right way.",
    primary: { label: "Talk to us", to: "mailto:support@photohawk.com" },
    secondary: { label: "Read our biometric data policy", to: "/biometric-data-retention" },
  },
};

const AuthorCard = ({ author, label }: { author: GuideAuthor; label: string }) => (
  <div className="flex gap-4">
    <div
      aria-hidden="true"
      className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-gradient-to-br from-hawk-orange to-hawk-gold font-display text-lg font-bold text-white"
    >
      {author.placeholder ? "?" : author.name.charAt(0)}
    </div>
    <div className="min-w-0">
      <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="m-0 font-display text-lg font-bold">
        <Link to={`${GUIDES_PATH}#${authorAnchor(author.id)}`} className="hover:text-hawk-orange">
          {author.name}
        </Link>
      </p>
      <p className="m-0 text-sm text-muted-foreground">{author.role}</p>
      <p className="mb-0 mt-2 text-sm leading-relaxed text-foreground/80">{author.bio}</p>
    </div>
  </div>
);

const Cta = ({ audience }: { audience: GuideAudience }) => {
  const c = CTA[audience];
  const btn = (to: string, label: string, primary: boolean) => {
    const cls = primary
      ? "inline-flex min-h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-hawk-orange to-hawk-gold px-6 py-3 font-semibold text-white shadow-glow hover:opacity-95"
      : "inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10";
    return to.startsWith("/") ? (
      <Link to={to} className={cls}>
        {label}
        {primary && <ArrowRight size={18} aria-hidden="true" />}
      </Link>
    ) : (
      <a href={to} className={cls}>
        {label}
        {primary && <ArrowRight size={18} aria-hidden="true" />}
      </a>
    );
  };
  return (
    <section className="relative overflow-hidden bg-hawk-navy py-16 text-white md:py-20" aria-labelledby="guide-cta">
      <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
      <div className="container relative z-10 mx-auto max-w-3xl px-4 text-center">
        <h2 id="guide-cta" className="mb-3 font-display text-3xl font-bold md:text-4xl">
          {c.heading}
        </h2>
        <p className="mb-8 text-white/80">{c.body}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {btn(c.primary.to, c.primary.label, true)}
          {c.secondary && btn(c.secondary.to, c.secondary.label, false)}
        </div>
      </div>
    </section>
  );
};

export const GuideArticle = ({ guide }: { guide: Guide }) => {
  const toc = tocOf(guide);
  const author = findAuthor(guide.author)!;
  const reviewer = findAuthor(guide.reviewer);
  const hero = GUIDE_IMAGES[guide.heroImage];
  const primary = AUDIENCES.find((a) => a.id === guide.audience[0])!;
  const related = guide.related.map((s) => getGuide(s, guide.lang)).filter((g): g is Guide => !!g);
  const pendingNotes = [
    guide.legalReview === "pending" ? "This guide is waiting for a legal review before it can go live." : null,
    author.placeholder || reviewer?.placeholder ? "The author or reviewer details are still TODO(owner) (src/content/guides/authors.ts)." : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      <main>
        <article data-testid="guide" lang={guide.lang}>
          {/* Header band */}
          <header className="relative overflow-hidden bg-hawk-navy pb-24 pt-28 text-white md:pb-32 md:pt-32">
            <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
            <div className="container relative z-10 mx-auto max-w-5xl px-4">
              <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
                <ol className="flex flex-wrap items-center gap-1 text-sm text-white/70">
                  <li>
                    <Link to="/" className="hover:text-white">
                      Home
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight size={14} />
                  </li>
                  <li>
                    <Link to={GUIDES_PATH} className="hover:text-white">
                      Guides
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <ChevronRight size={14} />
                  </li>
                  <li>
                    <Link to={`${GUIDES_PATH}#${primary.id}`} className="hover:text-white">
                      {primary.title}
                    </Link>
                  </li>
                </ol>
              </nav>
              <h1 className="mb-5 max-w-4xl font-display text-[clamp(2rem,5vw,3.25rem)] font-bold leading-[1.1] tracking-tight">{guide.title}</h1>
              <p className="mb-6 max-w-3xl text-lg text-white/80 md:text-xl">{guide.description}</p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/75">
                <span>
                  By{" "}
                  <Link to={`${GUIDES_PATH}#${authorAnchor(author.id)}`} className="font-semibold text-white hover:underline">
                    {author.name}
                  </Link>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <CalendarCheck size={15} aria-hidden="true" />
                  Last reviewed <time dateTime={guide.lastReviewed}>{formatGuideDate(guide.lastReviewed)}</time>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={15} aria-hidden="true" />
                  {readingMinutes(guide.words)} min read
                </span>
              </div>
            </div>
          </header>

          {/* Hero image, overlapping the band */}
          {hero && (
            <div className="container relative z-10 mx-auto -mt-16 max-w-5xl px-4 md:-mt-24">
              <figure className="m-0">
                <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-2xl">
                  <GuideImg img={hero} alt={guide.heroAlt} eager sizes="(min-width: 1024px) 992px, 100vw" />
                </div>
                {guide.heroCaption && (
                  <figcaption className="mt-2 text-sm text-muted-foreground [&_a]:underline [&_a]:underline-offset-2">{renderInline(guide.heroCaption, guideLink, "hero")}</figcaption>
                )}
              </figure>
            </div>
          )}

          <div className="container mx-auto max-w-5xl px-4 py-12 md:py-16">
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_15rem] lg:gap-12">
              <div className="min-w-0">
                {pendingNotes.length > 0 && (
                  <div role="note" data-testid="guide-pending" className="mb-8 max-w-[68ch] rounded-xl border-2 border-[#B54708] bg-[#FEF0C7] px-4 py-3 text-sm text-[#4E1D09]">
                    <strong className="block">Staging only, not published</strong>
                    {pendingNotes.map((n) => (
                      <span key={n} className="block">
                        {n}
                      </span>
                    ))}
                  </div>
                )}

                {/* Table of contents: inline on phones and tablets, in the sidebar on wide screens. */}
                {toc.length > 3 && (
                  <details className="guide-toc-mobile mb-8 max-w-[68ch] rounded-xl border border-border bg-card lg:hidden">
                    <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-4 py-3 font-semibold">
                      <ListOrdered size={18} className="text-hawk-orange" aria-hidden="true" />
                      In this guide
                    </summary>
                    <ol className="m-0 list-none space-y-1 px-4 pb-4">
                      {toc.map((t) => (
                        <li key={t.id}>
                          <a href={`#${t.id}`} className="block py-1 text-sm text-foreground/80 hover:text-hawk-orange">
                            {t.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </details>
                )}

                <div className="guide-prose" data-testid="guide-body">
                  <GuideBlocks blocks={guide.blocks} />

                  {guide.faq.length > 0 && (
                    <section aria-labelledby="faq" data-testid="guide-faq">
                      <h2 id="faq">Questions people ask</h2>
                      {guide.faq.map((f, i) => (
                        <div key={i} className="guide-faq">
                          <h3>{f.q}</h3>
                          <p>{renderInline(f.a, guideLink, `faq${i}`)}</p>
                        </div>
                      ))}
                    </section>
                  )}
                </div>

                {/* Who wrote it */}
                <section aria-label="About this guide" className="mt-12 max-w-[68ch] space-y-6 rounded-2xl border border-border bg-card p-6" data-testid="guide-authors">
                  <AuthorCard author={author} label="Written by" />
                  {reviewer && reviewer.id !== author.id && <AuthorCard author={reviewer} label="Reviewed by" />}
                  <p className="m-0 border-t border-border pt-4 text-sm text-muted-foreground">
                    Published <time dateTime={guide.published}>{formatGuideDate(guide.published)}</time> · Updated{" "}
                    <time dateTime={guide.updated}>{formatGuideDate(guide.updated)}</time> · Last reviewed{" "}
                    <time dateTime={guide.lastReviewed}>{formatGuideDate(guide.lastReviewed)}</time>
                  </p>
                </section>
              </div>

              {toc.length > 3 && (
                <aside className="hidden lg:block" aria-label="In this guide">
                  <nav className="sticky top-28">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">In this guide</p>
                    <ol className="m-0 list-none space-y-1 border-l border-border p-0">
                      {toc.map((t) => (
                        <li key={t.id}>
                          <a href={`#${t.id}`} className="-ml-px block border-l-2 border-transparent py-1 pl-3 text-sm text-foreground/75 hover:border-hawk-orange hover:text-hawk-orange">
                            {t.text}
                          </a>
                        </li>
                      ))}
                    </ol>
                  </nav>
                </aside>
              )}
            </div>
          </div>
        </article>

        {related.length > 0 && (
          <section className="border-t border-border bg-secondary/30 py-14" aria-labelledby="related">
            <div className="container mx-auto max-w-5xl px-4">
              <h2 id="related" className="mb-6 font-display text-2xl font-bold">
                Related guides
              </h2>
              <ul className="grid list-none gap-4 p-0 md:grid-cols-2">
                {related.map((r) => (
                  <li key={r.slug}>
                    <GuideCard guide={r} />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <Cta audience={guide.audience[0]} />
      </main>
      <Footer />
    </div>
  );
};

/** A guide's card on the hub and under "Related guides". */
export const GuideCard = ({ guide, headingLevel = 3 }: { guide: Guide; headingLevel?: 2 | 3 }) => {
  const img = GUIDE_IMAGES[guide.heroImage];
  const H = `h${headingLevel}` as "h2" | "h3";
  return (
    <Link
      to={guidePath(guide.slug)}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-lg"
    >
      {img && (
        <div className="aspect-[16/9] overflow-hidden bg-muted">
          <GuideImg img={img} alt="" sizes="(min-width: 768px) 480px, 100vw" className="block h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <H className="mb-2 font-display text-xl font-bold leading-snug group-hover:text-hawk-orange">{guide.title}</H>
        <p className="mb-4 flex-1 text-sm text-muted-foreground">{guide.description}</p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(18_100%_38%)]">
          Read the guide <span className="font-normal text-foreground/70">· {readingMinutes(guide.words)} min</span>
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};

/** /guides/:slug — a real 404 for an unknown slug, and everywhere the guides are off (prod until launch). */
const GuidePage = () => {
  const { slug = "" } = useParams();
  const { search } = useLocation();
  const lang = langFromSearch(search, guideLangs(slug)) ?? "en";
  const guide = getGuide(slug, lang);
  return <GuidesOnly>{guide ? <GuideArticle guide={guide} /> : <NotFound />}</GuidesOnly>;
};

export default GuidePage;
